import {
  normalizeDecision,
  normalizePromotion,
  normalizeThread,
  normalizeTurn,
  sortDecisionsAscending,
  sortThreads,
  sortTurnsAscending,
  sortTurnsDescending
} from "./conversation.js";
import { chooseTurnDetailArtifactID } from "./turn-detail.js";

export function normalizeHarnesses(items) {
  const normalized = [];
  const seenIDs = new Set();
  const seenSignatures = new Set();
  for (const raw of items || []) {
    const item = {
      id: String(raw.id || "").trim(),
      label: String(raw.label || "").trim(),
      adapter_id: String(raw.adapter_id || "").trim(),
      mode: String(raw.mode || "").trim(),
      server_url: String(raw.server_url || "").trim()
    };
    const idKey = item.id;
    const signature = `${item.adapter_id}|${item.mode}|${item.server_url.toLowerCase()}`;
    if (idKey && seenIDs.has(idKey)) {
      continue;
    }
    if (seenSignatures.has(signature)) {
      continue;
    }
    if (idKey) {
      seenIDs.add(idKey);
    }
    seenSignatures.add(signature);
    normalized.push(item);
  }
  return normalized;
}

export function sortPromotionsDescending(items) {
  return [...items].sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0));
}

export function normalizeAdminConsoleReadModel(snapshot) {
  return {
    agents: Array.isArray(snapshot?.agents) ? snapshot.agents : [],
    harnesses: normalizeHarnesses(snapshot?.harnesses || []),
    workspaces: Array.isArray(snapshot?.workspaces) ? snapshot.workspaces : [],
    threads: sortThreads((snapshot?.threads || []).map(normalizeThread)),
    turns: sortTurnsDescending((snapshot?.turns || []).map(normalizeTurn)),
    promotions: sortPromotionsDescending((snapshot?.promotions || []).map(normalizePromotion)),
    pendingDecisions: sortDecisionsAscending((snapshot?.pending_decisions || []).map(normalizeDecision)),
    inferenceEnabled: Boolean(snapshot?.inference_enabled),
    inferenceCredentials: Array.isArray(snapshot?.inference_credentials) ? snapshot.inference_credentials : [],
    inferenceRoutes: Array.isArray(snapshot?.inference_routes) ? snapshot.inference_routes : [],
    inferenceTraffic: Array.isArray(snapshot?.inference_traffic) ? snapshot.inference_traffic : []
  };
}

export function normalizeThreadReadModel(snapshot) {
  const eventsByTurnID = {};
  const eventSeqsByTurnID = {};
  const decisionsByTurnID = {};
  for (const [turnID, items] of Object.entries(snapshot?.events_by_turn_id || {})) {
    const events = Array.isArray(items) ? items : [];
    eventsByTurnID[turnID] = events;
    eventSeqsByTurnID[turnID] = new Set(events.map((item) => item.seq));
  }
  for (const [turnID, items] of Object.entries(snapshot?.decisions_by_turn_id || {})) {
    decisionsByTurnID[turnID] = sortDecisionsAscending((items || []).map(normalizeDecision));
  }
  return {
    thread: snapshot?.thread ? normalizeThread(snapshot.thread) : null,
    workspace: snapshot?.workspace || null,
    turns: sortTurnsAscending((snapshot?.turns || []).map(normalizeTurn)),
    eventsByTurnID,
    eventSeqsByTurnID,
    decisionsByTurnID
  };
}

export function normalizeTurnReadModel(snapshot, selectedArtifactID, options = {}) {
  const artifacts = Array.isArray(snapshot?.artifacts) ? snapshot.artifacts : [];
  const events = Array.isArray(snapshot?.events) ? snapshot.events : [];
  return {
    turn: snapshot?.turn ? normalizeTurn(snapshot.turn) : null,
    events,
    eventSeqs: new Set(events.map((item) => item.seq)),
    artifacts,
    decisions: sortDecisionsAscending((snapshot?.decisions || []).map(normalizeDecision)),
    selectedArtifactID: chooseTurnDetailArtifactID(selectedArtifactID, artifacts),
    artifactBody: "",
    artifactError: "",
    streamState: "idle",
    notFound: false,
    activeTab: String(options.activeTab || "output")
  };
}

export function normalizeTurnRefreshReadModel(snapshot) {
  return {
    turn: snapshot?.turn ? normalizeTurn(snapshot.turn) : null,
    artifacts: Array.isArray(snapshot?.artifacts) ? snapshot.artifacts : [],
    decisions: sortDecisionsAscending((snapshot?.decisions || []).map(normalizeDecision)),
    events: Array.isArray(snapshot?.events) ? snapshot.events : null
  };
}
